import {Article, Product} from '../../@types/wishlist.js';

export class SortAction {
  readonly compareStrategy: (p1: Product, p2: Product) => number;

  constructor(compareStrategy: (p1: Product, p2: Product) => number) {
    this.compareStrategy = compareStrategy;
  }

  public static createArticleCriteria(
    criteria: (article: Article) => number,
    findRelevant: (...values: number[]) => number,
    defaultValue: number = 0,
  ): (product: Product) => number {
    return (product: Product) => {
      const isUnavailable = !product.price;
      if (isUnavailable) {
        return defaultValue;
      }
      if (product.articleId) {
        return findRelevant(...product.articles.filter(article => article.id === product.articleId)
          .map(article => criteria(article)));
      }
      return findRelevant(...product.articles.map(article => criteria(article)));
    };
  }

  public static createAscSortStrategy(
    criteria: (product: Product) => number,
  ): SortAction {
    return new SortAction((p1: Product, p2: Product) => SortAction.compare(criteria, true, p1, p2));
  }

  public static createDescSortStrategy(
    criteria: (product: Product) => number,
  ): SortAction {
    return new SortAction((p1: Product, p2: Product) => SortAction.compare(criteria, false, p1, p2));
  }

  public static createRecentlyAddedSortStrategy(): SortAction {
    return new SortAction((p1: Product, p2: Product) => {
      const p1Added = p1.cAdded ?? p1.added;
      const p2Added = p2.cAdded ?? p2.added;
      return p2Added.localeCompare(p1Added);
    });
  }

  private static readonly defaultSortStrategy = SortAction.createRecentlyAddedSortStrategy();

  private static compare(
    criteria: (product: Product) => number,
    isAscendingOrder: boolean,
    p1: Product,
    p2: Product,
  ): number {
    const valueP1 = criteria(p1);
    const valueP2 = criteria(p2);
    if (valueP1 > valueP2) {
      return isAscendingOrder ? 1 : -1;
    }

    if (valueP1 < valueP2) {
      return isAscendingOrder ? -1 : 1;
    }

    return SortAction.defaultSortStrategy.compareStrategy(p1, p2);
  }
}
